/** useMe(): who is signed in, from Static Web Apps' /.auth/me (the swa CLI emulates it locally). */
import { useQuery } from '@tanstack/react-query';
import { primaryRole, type Role } from '@shared/roles';

export interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

export interface Me {
  principal: ClientPrincipal | null;
  role: Role | null;
  roles: string[];
  name: string;
}

export async function fetchMe(): Promise<Me> {
  let principal: ClientPrincipal | null = null;
  try {
    const res = await fetch('/.auth/me', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (res.ok && (res.headers.get('content-type') ?? '').includes('json')) {
      principal = ((await res.json()) as { clientPrincipal: ClientPrincipal | null }).clientPrincipal ?? null;
    }
  } catch {
    principal = null;
  }
  const roles = principal?.userRoles ?? [];
  return { principal, roles, role: primaryRole(roles), name: displayName(principal?.userDetails ?? '') };
}

/** "caroline.z@abfselis.org" → "Caroline" */
export function displayName(userDetails: string): string {
  const local = userDetails.split('@')[0] ?? '';
  const first = local.split(/[._-]/)[0] ?? '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : userDetails;
}

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 5 * 60 * 1000, retry: false });
}

export function signInUrl(returnTo = '/'): string {
  return `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;
}

export const SIGN_OUT_URL = '/.auth/logout?post_logout_redirect_uri=/login';
